/**
 * Player-facing game settings, persisted to localStorage. Set from the title
 * screen (src/ui/titleScreen.ts) and read when the game boots — the renderer
 * pulls them at init (CRT overlay visibility, screen-shake). Because the game
 * only boots AFTER the menu, values chosen there apply on the very next start.
 *
 * Deliberately tiny + self-contained (no sim import) so it stays safe to read
 * from both the entry point and the render layer. More knobs will land here as
 * the settings screen grows.
 */

export interface GameSettings {
  /** CRT scanline overlay on/off. */
  crt: boolean;
  /** Camera/glyph shake on damage on/off. */
  screenShake: boolean;
}

const DEFAULTS: GameSettings = { crt: true, screenShake: true };
const KEY = 'chagat.settings';

let cache: GameSettings | null = null;

/** The current settings (cached; merged over defaults so new keys are safe). */
export function loadSettings(): GameSettings {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(KEY);
    cache = raw ? { ...DEFAULTS, ...(JSON.parse(raw) as Partial<GameSettings>) } : { ...DEFAULTS };
  } catch {
    cache = { ...DEFAULTS };
  }
  return cache;
}

/** Persist `patch` over the current settings and update the cache. */
export function saveSettings(patch: Partial<GameSettings>): GameSettings {
  const next = { ...loadSettings(), ...patch };
  cache = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* storage may be unavailable (private mode) — keep the in-memory cache */
  }
  return next;
}
